/**
 * Cleaner functions for `betterstart uninstall`.
 * Each function modifies a single file type and returns what was removed.
 */

import fs from 'node:fs'

// ============================================================================
// JSON Comment Stripper (shared with tsconfig scaffolder)
// ============================================================================

function stripJsonComments(input: string): string {
  let result = ''
  let i = 0
  while (i < input.length) {
    if (input[i] === '"') {
      let j = i + 1
      while (j < input.length) {
        if (input[j] === '\\') {
          j += 2
          continue
        }
        if (input[j] === '"') {
          j++
          break
        }
        j++
      }
      result += input.slice(i, j)
      i = j
    } else if (input[i] === '/' && input[i + 1] === '/') {
      const nl = input.indexOf('\n', i)
      i = nl === -1 ? input.length : nl
    } else if (input[i] === '/' && input[i + 1] === '*') {
      const end = input.indexOf('*/', i + 2)
      i = end === -1 ? input.length : end + 2
    } else {
      result += input[i]
      i++
    }
  }
  return result
}

// ============================================================================
// cleanTsconfig
// ============================================================================

/**
 * Remove all `@admin/*` path aliases from tsconfig.json.
 * Returns the list of removed alias keys.
 */
export function cleanTsconfig(tsconfigPath: string, aliasRoot = '@admin'): string[] {
  if (!fs.existsSync(tsconfigPath)) return []

  const raw = fs.readFileSync(tsconfigPath, 'utf-8')
  const stripped = stripJsonComments(raw).replace(/,\s*([\]}])/g, '$1')

  let tsconfig: Record<string, unknown>
  try {
    tsconfig = JSON.parse(stripped)
  } catch {
    return []
  }

  const compilerOptions = (tsconfig.compilerOptions ?? {}) as Record<string, unknown>
  const paths = (compilerOptions.paths ?? {}) as Record<string, string[]>

  const removed: string[] = []
  for (const key of Object.keys(paths)) {
    if (
      key.startsWith('@admin/') ||
      key === '@admin/*' ||
      key.startsWith(`${aliasRoot}/`) ||
      key === `${aliasRoot}/*`
    ) {
      removed.push(key)
      delete paths[key]
    }
  }

  if (removed.length === 0) return []

  if (Object.keys(paths).length === 0) {
    compilerOptions.paths = undefined
  } else {
    compilerOptions.paths = paths
  }
  tsconfig.compilerOptions = compilerOptions

  fs.writeFileSync(tsconfigPath, `${JSON.stringify(tsconfig, null, 2)}\n`, 'utf-8')
  return removed
}

// ============================================================================
// cleanCss
// ============================================================================

/**
 * Remove @source lines containing "admin" from a CSS file.
 * Returns the removed lines.
 */
export function cleanCss(cssPath: string, namespace = 'admin'): string[] {
  if (!fs.existsSync(cssPath)) return []

  const content = fs.readFileSync(cssPath, 'utf-8')
  const lines = content.split('\n')

  const sourcePattern = new RegExp(`^@source\\s+"[^"]*(?:admin|${namespace})[^"]*";\\s*$`)
  const removed: string[] = []
  const kept: string[] = []

  for (const line of lines) {
    if (sourcePattern.test(line)) {
      removed.push(line.trim())
    } else {
      kept.push(line)
    }
  }

  if (removed.length === 0) return []

  // Clean up resulting double blank lines
  const cleaned = kept.join('\n').replace(/\n{3,}/g, '\n\n')
  fs.writeFileSync(cssPath, cleaned, 'utf-8')
  return removed
}

// ============================================================================
// cleanEnvFile
// ============================================================================

/**
 * Remove BETTERSTART_* vars and the BetterStart header block from .env.local.
 * Returns the removed variable names.
 */
export function cleanEnvFile(envPath: string): string[] {
  if (!fs.existsSync(envPath)) return []

  const content = fs.readFileSync(envPath, 'utf-8')
  const lines = content.split('\n')
  const removed: string[] = []
  const kept: string[] = []

  // Track header block lines to remove
  const headerPattern = /^# =+$/
  const headerTextPattern = /^# BetterStart Admin$/

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!
    const trimmed = line.trim()

    // Remove BETTERSTART_* variable lines
    if (trimmed.match(/^BETTERSTART_\w+=/)) {
      const key = trimmed.split('=')[0]!
      removed.push(key)
      continue
    }

    // Remove header block: # ====... / # BetterStart Admin / # ====...
    if (headerPattern.test(trimmed)) {
      const next = lines[i + 1]?.trim()
      const afterNext = lines[i + 2]?.trim()
      if (next && headerTextPattern.test(next) && afterNext && headerPattern.test(afterNext)) {
        i += 2 // skip all 3 header lines
        continue
      }
    }

    // Remove section comment headers that only precede BETTERSTART_ vars
    // e.g., "# Authentication" followed by BETTERSTART_AUTH_SECRET
    if (trimmed.startsWith('#') && !headerPattern.test(trimmed)) {
      const nextNonEmpty = findNextNonEmptyLine(lines, i + 1)
      if (nextNonEmpty?.match(/^BETTERSTART_\w+=/)) {
        continue // skip this comment header
      }
    }

    kept.push(line)
  }

  if (removed.length === 0) return []

  // Clean up multiple blank lines
  const result = kept
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

  if (result === '') {
    // File is now empty — delete it
    fs.unlinkSync(envPath)
  } else {
    fs.writeFileSync(envPath, `${result}\n`, 'utf-8')
  }

  return removed
}

function findNextNonEmptyLine(lines: string[], startIndex: number): string | null {
  for (let i = startIndex; i < lines.length; i++) {
    const trimmed = lines[i]!.trim()
    if (trimmed !== '') return trimmed
  }
  return null
}
